export const en = {
  // ── Common ────────────────────────────────────────────────────────────────
  back: '← BACK',
  loading: 'LOADING',
  loadingDots: 'LOADING...',
  vs: 'VS',
  or: 'OR',
  leave: 'LEAVE',
  exp: 'EXP',
  lvl: 'LVL',
  copied: 'COPIED!',
  dicepit: 'DICEPIT',
  joiningRoom: 'JOINING ROOM...',
  connecting: 'Connecting...',
  online: 'ONLINE',
  connectingDots: 'CONNECTING...',
  redirecting: 'REDIRECTING...',
  userDataReset: '✓ USER DATA RESET SUCCESSFULLY',

  // ── Menu ──────────────────────────────────────────────────────────────────
  practice: 'PRACTICE',
  practiceNoExp: 'NO EXP — TRAIN YOUR SKILLS',
  easy: 'EASY',
  normal: 'NORMAL',
  hard: 'HARD',
  turnTimeout: 'TURN TIMEOUT',
  seconds: 'SECONDS',
  multiplayer: 'MULTIPLAYER',
  playerOnline: (count: number) => `${count} ${count === 1 ? 'PLAYER' : 'PLAYERS'} ONLINE`,
  searching: 'SEARCHING...',
  quickMatch: 'QUICK MATCH',
  createRoom: 'CREATE ROOM',
  roomCode: 'ROOM CODE',
  join: 'JOIN',
  failedMatch: 'Failed to find match. Try again.',
  failedCreateRoom: 'Failed to create room. Try again.',
  enterRoomCode: 'Enter room code',
  roomFull: 'Room is full',
  gameAlreadyStarted: 'Game already started',
  roomNoLongerExists: 'Room no longer exists',
  roomNotFound: 'Room not found',
  menuPractice: 'PRACTICE',
  menuMultiplayer: 'MULTIPLAYER',
  menuHowToPlay: 'HOW TO PLAY',
  menuRecords: 'RECORDS',
  menuLeaderboard: 'LEADERBOARD',
  menuSettings: 'SETTINGS',
  legal: 'LEGAL',
  security: 'SECURITY',
  signUp: 'SIGN UP',
  guestSaveHint: 'YOUR EXP & STATS LIVE ONLY ON THIS DEVICE',
  guestSaveCta: 'SIGN UP TO SAVE',
  expFormat: (exp: number, threshold: number) => `${exp} / ${threshold} EXP`,
  lvlFormat: (level: number) => `LVL ${level}`,

  // ── Slogans ───────────────────────────────────────────────────────────────
  slogans: [
    "SurvIve the Arena",
    "Only One Rolls Out",
    "Enter the PIt. Earn Your ExIt.",
    "FIght the Roll",
    "No Mercy In the PIt",
    "Leave the Arena or Break"
  ],

  // ── Game ───────────────────────────────────────────────────────────────────
  rolling: 'ROLLING...',
  opponentRolling: 'OPPONENT ROLLING...',
  placeDie: (timer: number | null) => `PLACE YOUR DIE!${timer !== null ? ` ${timer}s` : ''}`,
  opponentPlacing: (timer: number | null) => `OPPONENT PLACING...${timer !== null ? ` ${timer}s` : ''}`,
  waitingForOpponent: 'Waiting for opponent...',
  leaveGame: 'LEAVE GAME?',
  leaveWarning: 'Leaving a game in progress counts as a',
  defeat: 'DEFEAT',
  stay: 'STAY',
  rematchQuestion: 'REMATCH?',
  rematchWants: 'Your opponent wants a',
  rematch: 'REMATCH',
  rematchExclaim: 'REMATCH!',
  decline: 'DECLINE',
  accept: 'ACCEPT',
  waitingForOpponentDots: 'WAITING FOR OPPONENT...',
  pressReady: 'PRESS READY!',
  unready: '✗ UNREADY',
  ready: 'READY?',
  wait: 'WAIT',
  opponentPlacingShort: 'OPPONENT PLACING...',
  pitLord: 'PIT LORD',
  opponent: 'OPPONENT',
  go: 'GO!',
  getReady: 'Get Ready!',
  room: 'Room',
  multiplayerBattle: 'Multiplayer Battle',
  matchDice: 'Match dice to multiply power • Remove enemy dice',
  waiting: 'WAITING...',
  rematchDeclined: 'REMATCH DECLINED',
  levelLabel: (difficulty: string) => `LEVEL: ${difficulty}`,
  selectJokerValue: 'Select Die Value:',

  // ── Game messages (useGame hook) ──────────────────────────────────────────
  msgRolling: 'ROLLING...',
  msgYourTurn: 'YOUR TURN',
  msgSlaughtered: 'SLAUGHTERED!',
  msgLordRolling: 'LORD IS ROLLING...',
  msgCrushed: 'YOU GOT CRUSHED!',
  msgVictory: 'VICTORY!',
  msgDefeat: 'DEFEAT...',
  msgTie: "A TIE? CURSED!",
  msgReadyToRoll: 'READY TO ROLL?',
  msgBattleBegins: 'BATTLE BEGINS!',

  // ── Settings ──────────────────────────────────────────────────────────────
  settings: 'SETTINGS',
  audio: 'Audio',
  music: 'Music',
  musicOff: 'MUSIC: OFF',
  musicOn: 'MUSIC: ON',
  soundOff: 'SOUND: OFF',
  soundOn: 'SOUND: ON',
  diceSpeed: 'Dice Speed',
  arenaTheme: 'Arena Theme',
  warriorName: 'Warrior Name',
  signUpToChooseName: 'SIGN UP TO CHOOSE YOUR NAME',
  nameCannotBeChanged: 'NAME CANNOT BE CHANGED',
  account: 'Account',
  playingAsGuest: 'PLAYING AS GUEST — SIGN UP TO SAVE YOUR PROGRESS',
  createAccount: 'CREATE ACCOUNT',
  signedInAs: 'SIGNED IN AS',
  signOut: 'SIGN OUT',
  deleteAccount: 'DELETE ACCOUNT',
  deleteAccountConfirm: 'ARE YOU SURE YOU WANT TO PERMANENTLY DELETE YOUR ACCOUNT?',
  deleteAccountWarning: 'All your game data, match history, and progress will be permanently erased. This action cannot be undone.',
  yesDelete: 'YES, DELETE',
  cancel: 'CANCEL',
  deletingAccount: 'DELETING...',
  accountDeleted: 'ACCOUNT DELETED',
  language: 'Language',

  // ── Auth ──────────────────────────────────────────────────────────────────
  createAccountTitle: 'CREATE ACCOUNT',
  signInTitle: 'SIGN IN',
  resetPasswordTitle: 'RESET PASSWORD',
  signIn: 'SIGN IN',
  forgotPw: 'FORGOT PW',
  allFieldsRequired: 'All fields are required',
  passwordMinLength: (n: number) => `Password must be at least ${n} characters`,
  usernameMaxLength: (n: number) => `Username must be ${n} characters or less`,
  signUpFailed: 'Sign up failed. Try again.',
  emailPasswordRequired: 'Email and password are required',
  signInFailed: 'Sign in failed. Check your credentials.',
  enterEmail: 'Enter your email address',
  resetEmailSent: 'Password reset link sent! Check your email.',
  failedResetEmail: 'Failed to send reset email. Try again.',
  email: 'Email',
  emailPlaceholder: 'YOUR@EMAIL.COM',
  username: 'Username',
  warriorNamePlaceholder: 'WARRIOR NAME',
  characters: (current: number, max: number) => `${current}/${max} CHARACTERS`,
  password: 'Password',
  minimumChars: (n: number) => `MINIMUM ${n} CHARACTERS`,
  creating: 'CREATING...',
  alreadyHaveAccount: 'ALREADY HAVE AN ACCOUNT?',
  signingIn: 'SIGNING IN...',
  forgotPassword: 'FORGOT YOUR PASSWORD?',
  resetIt: 'RESET IT',
  noAccount: "DON'T HAVE AN ACCOUNT?",
  enterEmailReset: "ENTER YOUR EMAIL AND WE'LL SEND YOU A RESET LINK",
  sending: 'SENDING...',
  sendResetLink: 'SEND RESET LINK',
  rememberedPassword: 'REMEMBERED YOUR PASSWORD?',

  // ── Reset Password ────────────────────────────────────────────────────────
  newPassword: 'NEW PASSWORD',
  enterNewPassword: 'Enter a new password',
  passwordsNoMatch: 'Passwords do not match',
  failedUpdatePassword: 'Failed to update password. Try again.',
  passwordUpdated: 'PASSWORD UPDATED SUCCESSFULLY!',
  newPasswordLabel: 'New Password',
  confirmPassword: 'Confirm Password',
  updating: 'UPDATING...',
  updatePassword: 'UPDATE PASSWORD',

  // ── History ───────────────────────────────────────────────────────────────
  pitRecords: 'PIT RECORDS',
  noBattles: 'NO BATTLES RECORDED...',
  victory: 'VICTORY',
  draw: 'DRAW',

  // ── Leaderboard ───────────────────────────────────────────────────────────
  leaderboard: 'LEADERBOARD',
  player: 'PLAYER',
  wld: 'W / L / D',
  noPlayers: 'NO PLAYERS YET...',
  rankFirst: '1ST',
  rankSecond: '2ND',
  rankThird: '3RD',
  you: '(YOU)',

  // ── Chat ──────────────────────────────────────────────────────────────────
  chat: 'Chat',
  msgCount: (count: number) => `${count} msgs`,
  noMessages: 'No messages yet...',
  chatYou: 'YOU',
  typeMessage: 'Type a message...',
  send: 'Send',

  // ── Cookie Consent ────────────────────────────────────────────────────────
  cookieNotice: 'Cookie Notice',
  cookieDescription: 'We use Google Analytics cookies to understand how players use DicePit and improve the game experience. No personal data is sold or shared with advertisers.',
  cookieAccept: 'Accept',
  cookieDecline: 'Decline',

  // ── Room errors (App.tsx) ─────────────────────────────────────────────────
  roomIsFull: 'ROOM IS FULL',
  gameAlreadyStartedUpper: 'GAME ALREADY STARTED',
  roomNoLongerExistsUpper: 'ROOM NO LONGER EXISTS',
  roomNotFoundUpper: 'ROOM NOT FOUND',
  usernameTaken: 'Username is already taken. Choose a different name.',

  // ── Speed labels ──────────────────────────────────────────────────────────
  speedSlow: 'SLOW',
  speedNormal: 'NORMAL',
  speedFast: 'FAST',

  // ── How To Play ─────────────────────────────────────────────────────────
  howToPlay: 'HOW TO PLAY',
  aboutTheGame: 'ABOUT THE GAME',
  htpGoalTitle: '1. The Goal',
  htpGoalText: 'Fill your 3x3 grid with dice. The player with the highest total score at the end wins the battle in the Pit.',
  htpScoringTitle: '2. Scoring Multipliers',
  htpScoringText: 'When you place multiple dice of the same value in a single column, their total is multiplied by the number of matches:',
  htpCombatTitle: '3. Combat (Slaughter)',
  htpCombatText1: 'Placing a die in a column will',
  htpCombatRemove: 'REMOVE',
  htpCombatText2: "all dice of the same value from the opponent's matching column. Use this to sabotage their scores!",
  htpProgressionTitle: '4. Pit Progression',
  htpProgressionText: 'Win matches to earn EXP and level up. High difficulty and massive score gaps grant bonus EXP. Beware: Losing will drain your experience.',
  htpSuperDiceTitle: '5. Super Dice Mode',
  htpSuperDiceText: 'In Super Dice mode, there is a 20% chance to roll a special die instead of a normal one:',
  htpJoker: 'Joker: Place it anywhere to choose any value (1-6).',
  htpShield: 'Shield: Protects a column from destruction for 2 rounds.',
  htpBomb: 'Bomb: Completely clears a column on BOTH your grid and the opponent\'s grid.',
  htpReroll: 'Reroll: Gives you two consecutive rolls of normal dice to place.',
  htpWhatIs: 'What is DicePit?',
  htpWhatIsText: 'DicePit is a free online multiplayer dice battle game inspired by the ancient game of',
  htpWhatIsText2: '. Two players face off, placing dice into their grids while strategically destroying their opponent\'s dice. Simple to learn, surprisingly deep to master.',
  htpOriginsTitle: 'Ancient Origins',
  htpOriginsText1: 'Knucklebones is one of the oldest games in human history, dating back to at least 5000 BCE. Originally played with the ankle bones (astragali) of sheep, it was popular across ancient Greece, Rome, and countless cultures worldwide — from the Mongolian steppes to the Polynesian islands.',
  htpOriginsText2: 'The game was mentioned by Plato and Herodotus. Sophocles attributed its invention to the mythical hero Palamedes during the Trojan War, while Plato credited the Egyptian god Thoth. Ancient Greeks and Romans played it for fun, gambling, and even divination — each face of the bone carried a specific value and name, from the lucky "Aphrodite" throw to the dreaded "Dog."',
  htpCotlTitle: 'Cult of the Lamb',
  htpCotlText1: 'The modern digital revival of Knucklebones owes much to',
  htpCotlName: 'Cult of the Lamb',
  htpCotlText2: ', the 2022 roguelike game by Massive Monster. In Cult of the Lamb, Knucklebones appears as a dice minigame played against NPCs — two opponents take turns rolling dice and placing them in a 3x3 grid, with the twist that matching dice in the opponent\'s column get destroyed.',
  htpCotlText3: 'This minigame became a fan favorite, with many players spending more time at the Knucklebones table than on the main quest itself. Its addictive blend of luck and strategy captured what made the ancient game so enduring — and sparked a wave of standalone versions, including DicePit.',
  htpCulturesTitle: 'A Game Across Cultures',
  htpCulturesIntro: 'Variations of Knucklebones exist in nearly every corner of the world:',
  htpBrowsersTitle: 'From Bones to Browsers',
  htpBrowsersText1: "DicePit takes the core mechanic that made Knucklebones timeless — the tension between building your own score and destroying your opponent's — and brings it into a real-time online arena. No downloads, no installs. Just roll, place, slaughter, and climb the leaderboard.",
  htpBrowsersText2: '5,000 years of dice games, distilled into a browser tab.',

  // ── Legal ───────────────────────────────────────────────────────────────
  privacyPolicy: 'PRIVACY POLICY',
  termsOfService: 'TERMS OF SERVICE',
  legalLastUpdated: 'Last updated: February 2026',
  // Privacy
  privacyIntro: 'DicePit ("we", "us", "our") is an online dice battle game operated by Riza Sabuncu. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data when you use DicePit at dicepit.xyz (the "Service").',
  privacyAgree: 'By using the Service, you agree to the collection and use of information as described in this policy. If you do not agree, please do not use the Service.',
  privacyS1Title: '1. INFORMATION WE COLLECT',
  privacyS1aTitle: 'A. Information You Provide',
  privacyS1aName: 'When you play, you choose a display name. This is stored in our database and displayed during multiplayer sessions.',
  privacyS1aEmail: 'You may optionally register with an email address and password to secure your account. If you do not register, an anonymous account is created automatically.',
  privacyS1aChat: 'In multiplayer mode, you may send chat messages. These messages are logged along with your player ID, display name, and the room identifier.',
  privacyS1bTitle: 'B. Automatically Collected Information',
  privacyS1bIp: 'When you send a chat message in multiplayer, your IP address is captured and logged alongside the message for moderation and abuse prevention purposes.',
  privacyS1bProgress: 'Your level, experience points (EXP), wins, losses, and match history are stored on our servers in the database.',
  privacyS1bLocal: "We use your browser's localStorage to store game settings (theme, sound preferences, dice speed) and your cookie consent choice. This data remains on your device.",
  privacyS1bPlayerId: 'A randomly generated UUID is created via our authentication system to identify you across sessions. This is not linked to your real identity unless you register with an email address.',
  privacyS1bSentry: "When errors occur in the application, we automatically collect error details (type, message, stack trace), your browser and operating system information (name, version), screen resolution, the page URL where the error occurred, and recent user actions leading up to the error (such as clicks, page navigations, and network requests). Your IP address may be processed by Sentry's servers but is not stored beyond 90 days.",
  privacyS2Title: '2. HOW WE USE YOUR INFORMATION',
  privacyS2Intro: 'We use collected information for the following purposes:',
  privacyS2Items: [
    'To operate multiplayer game rooms (matchmaking, real-time gameplay, chat)',
    'To moderate chat and prevent abuse (IP logging)',
    'To maintain your game progress in our database (level, EXP, match results)',
    'To monitor and fix errors and bugs in the Service (Sentry)',
    'To analyze usage patterns and improve the Service (Google Analytics)',
    'To provide and improve the Service',
  ],
  privacyS2NoSell: 'We do not sell, rent, or share your personal information with third parties for marketing purposes.',
  privacyS2aTitle: '2A. COOKIES AND CONSENT',
  privacyS2aCookie1: 'When you first visit DicePit, we ask for your consent before setting any analytics cookies. Google Analytics cookies are only loaded if you click "Accept" on the cookie notice banner.',
  privacyS2aCookie2: 'If you accept, cookies such as _ga and _ga_* are set to distinguish unique users and throttle request rates. These expire after 2 years and 24 hours respectively.',
  privacyS2aCookie3: 'If you decline, no analytics cookies are set and no usage data is collected by Google Analytics. Your choice is stored locally and remembered for future visits.',
  privacyS2aCookie4: "You can change your preference at any time by clearing your browser's localStorage (this will also reset your cookie consent choice).",
  privacyS3Title: '3. DATA STORAGE AND SECURITY',
  privacyS3Server: 'Server-side data (player profiles, game progress, multiplayer rooms, participants, match results, chat messages) is stored on Supabase, a cloud database platform hosted on AWS infrastructure. Real-time multiplayer communication is handled via a dedicated WebSocket server. All data is encrypted in transit via TLS/SSL.',
  privacyS3Client: "Client-side data (game settings, theme preferences, cookie consent) is stored in your browser's localStorage and remains on your device. Clearing your browser data will delete this information.",
  privacyS3Disclaimer: 'While we implement reasonable security measures, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security of your data.',
  privacyS4Title: '4. DATA RETENTION',
  privacyS4Chat: 'Chat messages and IP logs are retained indefinitely for moderation purposes. Multiplayer room data (participants, game state) is retained for the duration of the game session and may persist in the database afterward.',
  privacyS4Sentry: 'Error reports collected by Sentry are retained for up to 90 days.',
  privacyS4Local: "Local data (settings, theme preferences) persists until you clear your browser's localStorage. Server-side game progress persists until you request its deletion.",
  privacyS5Title: '5. YOUR RIGHTS',
  privacyS5Intro: 'Depending on your jurisdiction, you may have the right to:',
  privacyS5Items: [
    'Access the personal data we hold about you',
    'Request correction of inaccurate data',
    'Request deletion of your data',
    'Object to or restrict processing of your data',
    'Data portability',
  ],
  privacyS5Contact: 'To exercise any of these rights, contact us at the email address provided below. We will respond within 30 days.',
  privacyS5Local: "For local data (settings, preferences), you can delete it at any time by clearing your browser's localStorage. For server-side data (game progress, account), you can permanently delete your account from the Settings page.",
  privacyS6Title: "6. CHILDREN'S PRIVACY",
  privacyS6Text: 'The Service is not directed at children under 13. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will take steps to delete it.',
  privacyS7Title: '7. THIRD-PARTY SERVICES',
  privacyS7Intro: 'The Service uses the following third-party services:',
  privacyS7NoAds: 'We do not use advertising services.',
  privacyS8Title: '8. CHANGES TO THIS POLICY',
  privacyS8Text: 'We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. Your continued use of the Service after changes are posted constitutes acceptance of the updated policy.',
  privacyS9Title: '9. CONTACT',
  privacyS9Text: 'For privacy-related questions or requests, contact:',
  // Terms
  termsIntro: 'These Terms of Service ("Terms") govern your use of DicePit (the "Service"), an online dice battle game operated by Riza Sabuncu ("we", "us", "our") at dicepit.xyz.',
  termsAgree: 'By accessing or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.',
  termsS1Title: '1. ELIGIBILITY',
  termsS1Text: 'You must be at least 13 years old to use the Service. By using the Service, you represent that you meet this age requirement.',
  termsS2Title: '2. THE SERVICE',
  termsS2Text1: 'DicePit is a free-to-play browser-based dice game offering single-player (vs AI) and multiplayer (vs other players) modes. The Service is provided "as is" and "as available" without warranties of any kind.',
  termsS2Text2: 'We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time without prior notice.',
  termsS3Title: '3. USER ACCOUNTS',
  termsS3Text1: 'When you first visit the Service, an anonymous account is automatically created for you via our authentication system. You may optionally register with an email address and password to secure your account and access it from other devices.',
  termsS3Text2: "Your game progress (level, EXP, wins, losses) and player name are stored on our servers. Game settings and theme preferences are stored locally in your browser. We are not responsible for loss of local settings due to browser cache clearing.",
  termsS3Text3: 'You are responsible for any activity that occurs under your account, whether anonymous or registered.',
  termsS4Title: '4. ACCEPTABLE USE',
  termsS4Intro: 'When using the Service, you agree NOT to:',
  termsS4Items: [
    'Use the chat feature to harass, threaten, or abuse other players',
    'Send spam, offensive, or illegal content through chat',
    'Attempt to exploit, hack, or disrupt the Service or its infrastructure',
    'Use bots, scripts, or automation to interact with the Service',
    'Impersonate other players or misrepresent your identity',
    "Interfere with other players' enjoyment of the game",
  ],
  termsS4Ban: 'Violation of these rules may result in your IP being banned from the Service without prior notice.',
  termsS5Title: '5. CHAT AND USER CONTENT',
  termsS5Text1: 'The multiplayer chat feature allows communication between players. All chat messages are logged along with your IP address for moderation purposes.',
  termsS5Text2: 'You are solely responsible for the content of your messages. We reserve the right to review, delete, or restrict chat messages and ban users who violate these Terms.',
  termsS5Text3: 'By sending a chat message, you grant us a non-exclusive, royalty-free license to store and display your message within the game session.',
  termsS6Title: '6. INTELLECTUAL PROPERTY',
  termsS6Text1: 'All content, design, code, graphics, sounds, and other materials comprising the Service are the property of Riza Sabuncu and are protected by applicable copyright and intellectual property laws.',
  termsS6Text2: 'You may not copy, modify, distribute, sell, or create derivative works based on the Service without explicit written permission.',
  termsS7Title: '7. DISCLAIMER OF WARRANTIES',
  termsS7Text1: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
  termsS7Text2: 'We do not guarantee that the Service will be uninterrupted, error-free, or secure. We are not responsible for any data loss, game interruptions, or technical issues.',
  termsS8Title: '8. LIMITATION OF LIABILITY',
  termsS8Text1: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, REGARDLESS OF THE CAUSE OF ACTION.',
  termsS8Text2: 'Our total liability for any claim related to the Service shall not exceed the amount you paid to use the Service (which is zero, as the Service is free).',
  termsS9Title: '9. TERMINATION',
  termsS9Text1: 'We reserve the right to terminate or restrict your access to the Service at any time, for any reason, without notice. Upon termination, your right to use the Service ceases immediately.',
  termsS9Text2: 'Sections regarding intellectual property, disclaimers, limitation of liability, and governing law survive termination of these Terms.',
  termsS10Title: '10. GOVERNING LAW',
  termsS10Text: 'These Terms are governed by and construed in accordance with the laws of Turkey, without regard to conflict of law principles. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Turkey.',
  termsS11Title: '11. CHANGES TO TERMS',
  termsS11Text: 'We may update these Terms from time to time. Changes will be posted on this page with an updated date. Your continued use of the Service after changes are posted constitutes acceptance of the updated Terms.',
  termsS12Title: '12. CONTACT',
  termsS12Text: 'For questions about these Terms, contact:',

  // ── Security ────────────────────────────────────────────────────────────
  secBountyTitle: 'BUG BOUNTY PROGRAM',
  secBountyText: 'DicePit takes security seriously. If you discover a vulnerability in our game, we want to hear about it. We run an open bug bounty program to encourage responsible disclosure.',
  secReportTo: 'Report to:',
  secScopeTitle: 'SCOPE',
  secScopeIntro: 'The following targets are in scope:',
  secScopeApp: 'The entire DicePit web application including:',
  secScopeItems: [
    'Frontend application and client-side logic',
    'WebSocket game server (ws.dicepit.xyz) and real-time communication',
    'Database interactions and RLS policies',
    'Authentication and session management',
    'Leaderboard and EXP/ranking system integrity',
    'Chat system',
  ],
  secInScope: '• IN SCOPE',
  secOutOfScope: 'OUT OF SCOPE',
  secOutItems: [
    'Third-party services (Supabase infrastructure, Cloudflare, Google Analytics)',
    'Denial of Service (DoS/DDoS) attacks',
    'Social engineering attacks',
    "Attacks against other users' devices",
  ],
  secRewardTitle: 'REWARD',
  secRewardIntro: 'Valid vulnerability reports are rewarded with:',
  secRewardNote: 'Reward is per valid, unique vulnerability. Turkey only. Shipping not included.',
  secRulesTitle: 'RULES',
  secRulesItems: [
    "Do not disrupt the service or other players' experience",
    "Do not access, modify, or delete other players' data",
    'Provide a clear, detailed description and steps to reproduce',
    'Give us reasonable time to fix before public disclosure',
    'One reward per unique vulnerability',
    'First reporter of a vulnerability gets the reward',
  ],
  secFameTitle: 'HALL OF FAME',
  secFameIntro: 'Thanks to the following researchers for helping keep DicePit secure:',

  // ── Tutorial ──────────────────────────────────────────────────────────────
  tutS1: 'Welcome! This is your grid. Fill it with dice to score points.',
  tutS2: 'And this is your opponent\'s grid. Let\'s begin!',
  tutS3: 'You rolled a 3! Place it in the MIDDLE column.',
  tutS4: 'Your opponent places a die...',
  tutS5: 'Another 3! Put it in the same column to multiply your score!',
  tutS6: 'Your opponent places a 3 in the middle column...',
  tutS7: 'A 3 again! Place it in the middle column to DESTROY the opponent\'s 3!',
  tutS8: 'SLAUGHTERED! Your 3 destroyed the opponent\'s matching 3!',
  tutS9: 'Three 3\'s in one column = (3+3+3) × 3 = 27 points! Stack same values for big combos.',
  tutS10: 'You\'re ready for battle! Good luck in the Pit.',
  tutNext: 'NEXT',
  tutGotIt: 'GOT IT!',
  tutSkip: 'SKIP TUTORIAL',
  tutPlace: 'TAP THE HIGHLIGHTED COLUMN',
  tutTitle: 'TUTORIAL',
  tutMpTitle: 'FIRST TIME?',
  tutMpText: 'We recommend playing a quick tutorial game first to learn the ropes!',
  tutMpPlay: 'PLAY TUTORIAL',
  tutMpSkip: 'SKIP, I KNOW HOW',
  superDiceReleasePopupTitle: 'SUPER DICE RELEASED!',
  superDiceReleasePopupText: 'A brand new game mode is here! Experience Joker, Shield, Bomb, and Reroll dice.',
  superDiceReleasePopupPlay: 'PLAY SUPER DICE',
  superDiceReleasePopupClose: 'MAYBE LATER',
};

export type TranslationKeys = {
  [K in keyof typeof en]: (typeof en)[K] extends (...args: infer A) => infer R
  ? (...args: A) => R
  : (typeof en)[K] extends readonly string[]
  ? string[]
  : string;
};
